<script lang="ts" setup>
import type { LayoutAndNavQuery } from '@autobid/strapi-integration/graphql/graphql'
import type { components } from '@autobid/strapi-integration/rest/schema'
import type { Page, StrapiPageLocalizationData } from '@autobid/strapi-integration/typescript/strapi/Page'
import { useIsNoPrefixRoute } from '@autobid/strapi-integration/composable/useIsNoPrefixRoute'

interface Props {
  seoData: Page['seo'] | NonNullable<LayoutAndNavQuery['layouts'][number]>['seo'] | null
  localizations?: StrapiPageLocalizationData[]
}

const props = defineProps<Props>()
const {
  public: {
    SELF_URL,
    autobidCMS: { url: strapiDomain },
  },
} = useRuntimeConfig()
const prefix = inject('pageTitle', '')
const route = useRoute()
const { locale } = useI18n()
const isRouteWithoutLangPrefix = useIsNoPrefixRoute()
const canonicalLink = computed(() => {
  return props.seoData?.canonicalURL ?? isRouteWithoutLangPrefix.value
    ? `/${locale.value}${route.path}`
    : undefined
})

const fbData = computed(() => {
  return props.seoData?.metaSocial?.find(el => el?.socialNetwork === 'Facebook')
})

const twitterData = computed(() => {
  return props.seoData?.metaSocial?.find(el => el?.socialNetwork === 'Twitter')
})

function generateLocalizations() {
  return (props.localizations ?? []).map((lang) => {
    return {
      rel: 'alternate',
      hreflang: lang.locale,
      href: `${SELF_URL}/${lang.locale}/${
        lang.slug === '/' ? '' : lang.slug
      }`,
    }
  })
}

function getSocialTags(
  prefix: 'og' | 'twitter',
  data: components['schemas']['SharedMetaSocialEntry'] |
    NonNullable<NonNullable<NonNullable<LayoutAndNavQuery['layouts'][number]>['seo']>['metaSocial']>[number] |
    undefined |
    null,
) {
  return {
    [`${prefix}Title`]: () => data?.title,
    [`${prefix}Description`]: () => data?.description,
    [`${prefix}Image`]: () => data?.image ? `${strapiDomain}${data.image.url}` : undefined,
    [`${prefix}ImageAlt`]: () => data?.image?.alternativeText,
    [`${prefix}ImageHeight`]: () => data?.image?.height,
    [`${prefix}ImageWidth`]: () => data?.image?.width,
  }
}

useSeoMeta({
  title: () => {
    return props.seoData?.metaTitle
      ? `${props.seoData?.metaTitle}${prefix ? ` | ${prefix}` : ''}`
      : ''
  },
  description: () => props.seoData?.metaDescription,
  robots: () => props.seoData?.metaRobots,
  ogType: () => 'website',
  ogUrl: () => route.fullPath,
  twitterCard: () => 'summary_large_image',
  keywords: () => props.seoData?.keywords,
  ...getSocialTags('og', fbData.value),
  ...getSocialTags('twitter', twitterData.value),
})

useHead({
  link: [
    canonicalLink.value
      ? {
          rel: 'canonical',
          href: canonicalLink,
        }
      : {},
    generateLocalizations(),
  ].flat(),
})
</script>

<template>
  <!-- workaround: template can not be empty -->
  <span v-if="false" />
</template>
